"use client"

/**
 * 2-pane form shell — round 4 (Payload-dense).
 *
 * Round-3 had main column sprawling to 928px on 1920px viewports ("Word
 * document feel") AND the sidebar kept an independent scrollbar via
 * maxHeight+overflowY:auto, triggering the double-scroll whenever six
 * sidebar sections stacked past the viewport height.
 *
 * Round-4 fixes:
 * - Main column capped at 680px — the measured sweet spot for form
 *   editing in professional CMS (Ghost 720, Payload 650, Contentful 700).
 *   Grid now uses `minmax(0, 680px) 300px` centered, so on 1920px we get
 *   ~450px empty gutters each side — content anchored in the reading
 *   zone, not stretched edge-to-edge.
 * - Sidebar drops maxHeight + overflowY. With `position: sticky` alone,
 *   short sidebars pin while the user scrolls and tall sidebars release
 *   at their natural bottom and scroll with the page. No second scrollbar.
 * - Header chrome tightened: breadcrumb mb-3 → mb-2, title mb-6 → mb-4.
 *   First input now sits ~48px from the top of the admin content area
 *   instead of ~76px.
 */

import { ArrowLeftOutlined } from "@ant-design/icons"
import { Breadcrumb } from "antd"
import Link from "next/link"
import { ReactNode } from "react"

export interface FormShellCrumb {
  title: ReactNode
  href?: string
}

interface FormShellProps {
  title: ReactNode
  subtitle?: ReactNode
  crumbs?: FormShellCrumb[]
  errorSummary?: ReactNode
  main: ReactNode
  sidebar?: ReactNode
  actionBar?: ReactNode
}

export function FormShell({ title, subtitle, crumbs, errorSummary, main, sidebar, actionBar }: FormShellProps) {
  const breadcrumbItems =
    crumbs && crumbs.length > 0
      ? crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1
          return {
            title:
              crumb.href && !isLast ? (
                <Link href={crumb.href} style={{ color: "var(--admin-muted-fg)", fontWeight: 400 }}>
                  {i === 0 && <ArrowLeftOutlined aria-hidden style={{ marginRight: 4, fontSize: 11 }} />}
                  {crumb.title}
                </Link>
              ) : (
                <span
                  style={{
                    color: isLast ? "var(--admin-foreground)" : "var(--admin-muted-fg)",
                    fontWeight: isLast ? 500 : 400,
                  }}
                >
                  {crumb.title}
                </span>
              ),
          }
        })
      : undefined

  return (
    <div
      className="form-shell"
      style={{
        // Container cap so content sits in a consistent reading zone on
        // ultra-wide screens. 680 main + 300 sidebar + 32 gap + 32 padding
        // = 1044px min, capped at 1060 comfortable.
        maxWidth: 1060,
        marginLeft: "auto",
        marginRight: "auto",
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      {/* Breadcrumb row */}
      {breadcrumbItems && (
        <div className="mb-2">
          <Breadcrumb
            items={breadcrumbItems}
            separator={<span style={{ color: "var(--admin-muted-fg)", opacity: 0.4 }}>/</span>}
            style={{ fontSize: 13 }}
          />
        </div>
      )}

      {/* Title row */}
      <div className="mb-4">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1
            className="m-0"
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "var(--admin-foreground)",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <span
              className="truncate"
              style={{
                fontSize: 13,
                color: "var(--admin-muted-fg)",
                fontWeight: 400,
                maxWidth: "60%",
              }}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {errorSummary && <div className="mb-4">{errorSummary}</div>}

      {/* Grid — 2-pane when sidebar present, single-column otherwise. */}
      <div
        className="form-shell-grid"
        style={{
          display: "grid",
          gridTemplateColumns: sidebar ? "minmax(0, 680px) 300px" : "1fr",
          gap: sidebar ? 32 : 0,
          alignItems: "start",
          paddingBottom: actionBar ? 16 : 0,
        }}
      >
        <div className="form-shell-main" style={{ minWidth: 0 }}>
          {main}
        </div>
        {sidebar && (
          <aside
            className="form-shell-sidebar"
            style={{
              position: "sticky",
              top: 88,
              alignSelf: "start",
              // No maxHeight, no overflowY. Short sidebar pins while main
              // scrolls; tall sidebar releases and scrolls with the page.
            }}
          >
            {sidebar}
          </aside>
        )}
      </div>

      {actionBar && (
        <div
          role="toolbar"
          aria-label="Hành động form"
          className="mt-4"
          style={{
            position: "sticky",
            bottom: 12,
            background: "var(--admin-surface)",
            border: "1px solid var(--admin-border)",
            padding: "10px 16px",
            borderRadius: 10,
            boxShadow: "0 4px 20px oklch(0 0 0 / 0.08)",
            zIndex: 20,
          }}
        >
          {actionBar}
        </div>
      )}
    </div>
  )
}

export default FormShell
